import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/i18n";
import LanguageSelector from "@/components/LanguageSelector";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { errMsg } from "@/lib/api";
import { usePublicBranding } from "@/lib/platformConfig";
import { GraduationCap, Users, Wallet, Megaphone, ArrowRight, Sparkles } from "lucide-react";

const DEMO = [
  { email: "principal@greenwood.edu.in", label: "Principal", role: "Anita Sharma" },
  { email: "administrator@greenwood.edu.in", label: "Administrator", role: "Nursery operations" },
  { email: "teacher1@greenwood.edu.in", label: "Teacher", role: "Priya Reddy (Class 5A)" },
  { email: "parent1@greenwood.edu.in", label: "Parent", role: "Multi-ward guardian" },
  { email: "accountant@greenwood.edu.in", label: "Accountant", role: "Ravi Kumar" },
];

export default function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  const { t } = useI18n();
  const branding = usePublicBranding();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const appName = branding.data?.app_name || "EduOS";
  const clientName = branding.data?.client_name || "Education Operating System";
  const appInitial = (appName || "E").trim().slice(0, 1).toUpperCase() || "E";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setSubmitting(true);
    try {
      const session = await login(email, password);
      toast.success(t("login.welcome"));
      nav(session?.must_change_password ? "/change-password" : "/");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSubmitting(false);
    }
  };

  const applyDemo = (demoEmail) => {
    setEmail(demoEmail);
    setPassword("");
  };

  return (
    <div className="min-h-screen grid md:grid-cols-5 bg-[var(--bg)]">
      {/* Left: Brand storytelling */}
      <div className="md:col-span-3 relative overflow-hidden bg-[var(--brand)] text-white grain-overlay">
        <div className="relative z-10 h-full px-8 md:px-16 py-12 md:py-20 flex flex-col justify-between">
          <div className="flex items-center gap-2">
            <div className="h-10 w-10 rounded-md bg-[var(--accent)] flex items-center justify-center font-display font-bold text-xl">{appInitial}</div>
            <div>
              <div className="font-display text-2xl font-bold leading-none">{appName}</div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-white/60">{clientName}</div>
            </div>
          </div>

          <div className="my-10">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-3 py-1 text-xs font-medium">
              <Sparkles className="h-3.5 w-3.5 text-[var(--accent)]" />
              {t("login.platformBadge")}
            </div>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.02] tracking-tight">
              {t("login.heroLead")} <span className="text-[var(--accent)]">{t("login.heroAccent")}</span>
            </h1>
            <p className="mt-6 text-white/70 text-lg max-w-lg leading-relaxed">
              {t("login.heroDescription")}
            </p>

            <div className="mt-10 grid grid-cols-2 gap-4 max-w-lg">
              {[
                { icon: GraduationCap, title: t("login.admissionFeature"), sub: t("login.admissionFeatureDetail") },
                { icon: Users, title: t("login.recordsFeature"), sub: t("login.recordsFeatureDetail") },
                { icon: Wallet, title: t("login.feesFeature"), sub: t("login.feesFeatureDetail") },
                { icon: Megaphone, title: t("login.circularsFeature"), sub: t("login.circularsFeatureDetail") },
              ].map((f, i) => (
                <div key={i} className="rounded-md border border-white/10 bg-white/5 p-4 backdrop-blur-sm">
                  <f.icon className="h-5 w-5 text-[var(--accent)]" />
                  <div className="mt-3 font-semibold text-sm">{f.title}</div>
                  <div className="text-white/60 text-xs">{f.sub}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-white/40 text-xs">
            {t("login.trustFooter")}
          </div>
        </div>
      </div>

      {/* Right: Login card */}
      <div className="md:col-span-2 flex items-center justify-center px-6 py-12 md:py-0">
        <div className="w-full max-w-sm">
          <div className="flex justify-end mb-5">
            <LanguageSelector />
          </div>
          <div className="mb-8">
            <h2 className="font-display text-3xl font-bold text-[var(--text-primary)]">{t("login.signIn")}</h2>
            <p className="text-sm text-[var(--text-secondary)] mt-1">{t("login.access")}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
                {t("login.email")}
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("login.emailPlaceholder")}
                autoComplete="email"
                required
                className="mt-1.5 h-11"
                data-testid="login-email"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
                {t("login.password")}
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                className="mt-1.5 h-11"
                data-testid="login-password"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="login-submit"
              className="w-full h-11 bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white font-semibold"
            >
              {submitting ? t("login.submitting") : t("login.submit")} <ArrowRight className="ms-2 h-4 w-4" />
            </Button>
          </form>

          {branding.data?.demo_mode && <div className="mt-8 pt-6 border-t border-[var(--subtle)]">
            <div className="text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)] mb-3">
              {t("login.demos")}
            </div>
            <div className="grid gap-2">
              {DEMO.map((d) => (
                <button
                  key={d.email}
                  type="button"
                  onClick={() => applyDemo(d.email)}
                  data-testid={`demo-${d.label.toLowerCase()}`}
                  className="flex items-center justify-between text-left rounded-md border border-[var(--subtle)] bg-white hover:border-[var(--accent)] px-3 py-2 transition-colors"
                >
                  <div>
                    <div className="text-sm font-semibold">{d.label}</div>
                    <div className="text-xs text-[var(--text-muted)]">{d.role}</div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-[var(--text-muted)]" />
                </button>
              ))}
            </div>
            <div className="mt-3 text-[11px] text-[var(--text-muted)]">
              {t("login.demoPassword")} <span className="font-mono-num font-semibold">password123</span>
            </div>
          </div>}
        </div>
      </div>
    </div>
  );
}
